import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui-kit/card';
import { Button } from '@/components/ui-kit/button';
import { MarkdownEditor } from '@/modules/gpt-chats/components/markdown-editor';
import { MarkdownRenderer } from '@/modules/gpt-chats/components/markdown-renderer';
import { useSaveTutorial, useGetToolDetails } from '@/modules/gpt-chats/hooks/use-tools';
import { showSuccessToast, showErrorToast } from '@/hooks/use-toast';

import { useSearchParams } from '@/lib/react-router-compat';
import { ISaveTutorialPayload } from '@/modules/gpt-chats/types/tools.service.type';
import { useTranslation } from 'react-i18next';

const defaultContent = `# Tutorial Guide

Write your tutorial guide content here using Markdown...`;

export const ToolTutorialGuide = ({ toolId }: { toolId: string }) => {
  const searchParams = useSearchParams();
  const { t } = useTranslation();
  const isBlocksTool = searchParams.get('isBlocksTool') === 'true';
  const [content, setContent] = useState(defaultContent);
  const { mutate: saveTutorial, isPending } = useSaveTutorial();
  const { data: toolDetails } = useGetToolDetails(toolId, isBlocksTool);

  useEffect(() => {
    if (toolDetails?.Tutorial) {
      setContent(toolDetails.Tutorial);
    }
  }, [toolDetails]);

  const handleSave = () => {
    const payload: ISaveTutorialPayload = {
      tutorial: content,
      is_open_tool: isBlocksTool,
    };
    saveTutorial(
      { toolId, payload },
      {
        onSuccess: () => {
          showSuccessToast({ description: t('TUTORIAL_SAVED_SUCCESSFULLY') });
        },
        onError: (error: unknown) => {
          let errorMessage = t('FAILED_TO_SAVE_TUTORIAL');
          if (error && typeof error === 'object' && 'response' in error) {
            const response = (error as { response?: { data?: { detail?: string } } }).response;
            errorMessage = response?.data?.detail || errorMessage;
          } else if (error instanceof Error) {
            errorMessage = error.message;
          }
          showErrorToast({ errors: [{ message: errorMessage }] });
        },
      }
    );
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      {/* Edit Section */}
      <Card className="flex h-[calc(100vh-300px)] flex-col">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{t('EDIT')}</CardTitle>
          <Button onClick={handleSave} disabled={isPending} size="sm">
            {isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('SAVING')}
              </>
            ) : (
              t('SAVE')
            )}
          </Button>
        </CardHeader>
        <CardContent className="min-h-0 flex-1 overflow-hidden">
          <MarkdownEditor
            value={content}
            onChange={setContent}
            showCharacterCount={true}
            className="flex-1 resize-none"
            placeholder={t('WRITE_HERE')}
            disabled={isPending}
            showToolbar={true}
            showModeToggle={false}
            defaultMode="edit"
          />
        </CardContent>
      </Card>

      {/* Preview Section */}
      <Card className="flex h-[calc(100vh-300px)] flex-col">
        <CardHeader>
          <CardTitle>{t('PREVIEW')}</CardTitle>
        </CardHeader>
        <CardContent className="min-h-0 flex-1 overflow-y-auto">
          <div className="prose prose-sm max-w-none dark:prose-invert">
            <MarkdownRenderer content={content} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
